/**
 * PATH: packages/ui/src/stores/universalTabStore.ts
 * PURPOSE: Universal tab store for cross-hub tab management.
 *
 * WHY: Users need to open entities from ANY hub (jobs, quotes, customers,
 *      timesheets) as tabs in ONE window and switch between them instantly.
 *      This store manages tabs across all hubs with hub-color tagging.
 *
 * DESIGN:
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │ [●WF] People   [●WF] Allocation   [●CRM] John Smith   [●QB] Q-456│
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * DIFFERENCES FROM createTabStore:
 *   - SINGLE store instance (not per-hub)
 *   - Each tab has hubId, hubColor, hubLabel
 *   - Supports cross-hub tab opening via navigate()
 *   - Used by the unified shell (not individual hub layouts)
 *
 * DEPENDENCIES:
 *   - zustand: State management
 *   - zustand/middleware: persist for localStorage
 *   - unifiedHubConfig: Hub color/label definitions
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { UNIFIED_HUB_CONFIGS, getHubIdFromPath, type HubColor } from '../config/unifiedHubConfig'

// ─── Types ─────────────────────────────────────────────────

export interface UniversalTab {
  /** Unique tab ID */
  id: string
  /** Hub this tab belongs to */
  hubId: string
  /** Full path (e.g., '/workforce/people/123') */
  path: string
  /** Display title (e.g., 'John Smith') */
  title: string
  /** Icon key for rendering */
  iconKey?: string
  /** Hub color for the tab badge */
  hubColor: HubColor
  /** Hub label for the tab subtitle */
  hubLabel: string
  /** Pinned tabs can't be closed */
  isPinned: boolean
  /** Whether the tab has unsaved changes */
  isDirty: boolean
  /** Remembered scroll position */
  scrollY?: number
  /** Timestamp when the tab was last activated */
  lastActiveAt: number
}

export interface UniversalTabState {
  tabs: UniversalTab[]
  activeTabId: string | null
  maxTabs: number
}

export interface UniversalTabActions {
  /** Open a new tab (or activate existing one for the same path) */
  openTab: (path: string, title?: string, iconKey?: string) => string
  /** Close a tab by ID */
  closeTab: (tabId: string) => void
  /** Set the active tab */
  setActiveTab: (tabId: string) => void
  /** Toggle pin state */
  togglePin: (tabId: string) => void
  /** Close all tabs except the given one (and pinned) */
  closeOthers: (tabId: string) => void
  /** Duplicate a tab */
  duplicateTab: (tabId: string) => void
  /** Update the active tab's path/title (for in-tab navigation) */
  updateActiveTab: (path: string, title?: string, iconKey?: string) => void
  /** Save scroll position */
  saveScrollPosition: (tabId: string, scrollY: number) => void
  /** Mark a tab as dirty */
  setDirty: (tabId: string, isDirty: boolean) => void
  /** Get all tabs for a specific hub */
  getHubTabs: (hubId: string) => UniversalTab[]
  /** Get the most recent tab for a hub */
  getMostRecentHubTab: (hubId: string) => UniversalTab | undefined
  /** Close all tabs for a specific hub */
  closeHubTabs: (hubId: string) => void
}

export type UniversalTabStore = UniversalTabState & UniversalTabActions

// ─── Helpers ───────────────────────────────────────────────

function generateTabId(): string {
  return `utab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function createTabFromPath(path: string, title?: string, iconKey?: string): UniversalTab {
  const hubId = getHubIdFromPath(path)
  const hubConfig = UNIFIED_HUB_CONFIGS[hubId]

  return {
    id: generateTabId(),
    hubId,
    path,
    title: title || getDefaultTitle(path, hubId),
    iconKey,
    hubColor: hubConfig?.color || 'slate',
    hubLabel: hubConfig?.label || 'Dashboard',
    isPinned: false,
    isDirty: false,
    lastActiveAt: Date.now(),
  }
}

function getDefaultTitle(path: string, hubId: string): string {
  // Extract the last meaningful segment
  const segments = path.replace(/^\//, '').split('/').filter(Boolean)
  if (segments.length <= 1) {
    return UNIFIED_HUB_CONFIGS[hubId]?.label || 'New Tab'
  }
  // Use the segment after the hub prefix
  const pageName = segments[1] || ''
  return pageName.charAt(0).toUpperCase() + pageName.slice(1).replace(/-/g, ' ')
}

// ─── Store ─────────────────────────────────────────────────

export const useUniversalTabStore = create<UniversalTabStore>()(
  persist(
    (set, get) => ({
      // Initial state — starts empty, tabs are created on navigation
      tabs: [],
      activeTabId: null,
      maxTabs: 20,

      openTab: (path, title, iconKey) => {
        const { tabs, maxTabs } = get()

        // Check if a tab with this exact path exists
        const existing = tabs.find(t => t.path === path)
        if (existing) {
          set({
            activeTabId: existing.id,
            tabs: tabs.map(t =>
              t.id === existing.id ? { ...t, lastActiveAt: Date.now() } : t
            ),
          })
          return existing.id
        }

        // Create new tab
        const newTab = createTabFromPath(path, title, iconKey)
        let updatedTabs = [...tabs, newTab]

        // Enforce max tabs — close oldest unpinned non-active tab
        const unpinned = updatedTabs.filter(t => !t.isPinned)
        if (unpinned.length > maxTabs) {
          const oldest = unpinned
            .filter(t => t.id !== newTab.id && t.id !== get().activeTabId)
            .sort((a, b) => a.lastActiveAt - b.lastActiveAt)[0]
          if (oldest) {
            updatedTabs = updatedTabs.filter(t => t.id !== oldest.id)
          }
        }

        set({ tabs: updatedTabs, activeTabId: newTab.id })
        return newTab.id
      },

      closeTab: (tabId) => {
        const { tabs, activeTabId } = get()
        const tab = tabs.find(t => t.id === tabId)

        // Can't close pinned tabs
        if (!tab || tab.isPinned) return
        // Don't close the last tab
        if (tabs.length <= 1) return

        const tabIndex = tabs.findIndex(t => t.id === tabId)
        const updatedTabs = tabs.filter(t => t.id !== tabId)

        // If closing active tab, switch to adjacent
        let newActiveId = activeTabId
        if (activeTabId === tabId) {
          const nextTab = tabs[tabIndex + 1] || tabs[tabIndex - 1] || updatedTabs[0]
          newActiveId = nextTab?.id ?? null
        }

        set({ tabs: updatedTabs, activeTabId: newActiveId })
      },

      setActiveTab: (tabId) => {
        const tab = get().tabs.find(t => t.id === tabId)
        if (tab) {
          set({
            activeTabId: tabId,
            tabs: get().tabs.map(t =>
              t.id === tabId ? { ...t, lastActiveAt: Date.now() } : t
            ),
          })
        }
      },

      togglePin: (tabId) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isPinned: !t.isPinned } : t
          ),
        }))
      },

      closeOthers: (tabId) => {
        set((state) => ({
          tabs: state.tabs.filter(t => t.id === tabId || t.isPinned),
          activeTabId: tabId,
        }))
      },

      duplicateTab: (tabId) => {
        const { tabs } = get()
        const source = tabs.find(t => t.id === tabId)
        if (!source) return

        const newTab: UniversalTab = {
          ...source,
          id: generateTabId(),
          isPinned: false,
          isDirty: false,
          lastActiveAt: Date.now(),
        }

        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: newTab.id,
        }))
      },

      updateActiveTab: (path, title, iconKey) => {
        const { activeTabId } = get()
        if (!activeTabId) return

        const hubId = getHubIdFromPath(path)
        const hubConfig = UNIFIED_HUB_CONFIGS[hubId]

        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === activeTabId
              ? {
                  ...t,
                  path,
                  hubId,
                  hubColor: hubConfig?.color || t.hubColor,
                  hubLabel: hubConfig?.label || t.hubLabel,
                  ...(title !== undefined && { title }),
                  ...(iconKey !== undefined && { iconKey }),
                }
              : t
          ),
        }))
      },

      saveScrollPosition: (tabId, scrollY) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, scrollY } : t
          ),
        }))
      },

      setDirty: (tabId, isDirty) => {
        set((state) => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? { ...t, isDirty } : t
          ),
        }))
      },

      getHubTabs: (hubId) => {
        return get().tabs.filter(t => t.hubId === hubId)
      },

      getMostRecentHubTab: (hubId) => {
        const hubTabs = get().tabs.filter(t => t.hubId === hubId)
        if (hubTabs.length === 0) return undefined
        return hubTabs.sort((a, b) => b.lastActiveAt - a.lastActiveAt)[0]
      },

      closeHubTabs: (hubId) => {
        const { tabs, activeTabId } = get()
        const remaining = tabs.filter(t => t.hubId !== hubId || t.isPinned)
        const activeRemoved = !remaining.some(t => t.id === activeTabId)

        set({
          tabs: remaining,
          activeTabId: activeRemoved ? remaining[0]?.id ?? null : activeTabId,
        })
      },
    }),
    {
      name: 'quotech-universal-tabs',
      partialize: (state) => ({
        tabs: state.tabs.map(({ scrollY, ...rest }) => rest),
        activeTabId: state.activeTabId,
        maxTabs: state.maxTabs,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<UniversalTabState>
        const merged = { ...currentState, ...persisted }

        // Validate tabs array
        if (!Array.isArray(merged.tabs)) {
          merged.tabs = []
        }

        // Validate activeTabId
        if (merged.activeTabId && !merged.tabs.some(t => t.id === merged.activeTabId)) {
          merged.activeTabId = merged.tabs[0]?.id ?? null
        }

        return merged as UniversalTabStore
      },
    }
  )
)

export default useUniversalTabStore
